import * as React from 'react';
import { NumberField as BaseNumberField } from '@base-ui/react/number-field';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';

/**
 * This component is a placeholder for FormControl to correctly set the shrink label state on SSR.
 */
function SSRInitialFilled(_: BaseNumberField.Root.Props) {
  return null;
}
SSRInitialFilled.muiName = 'Input';

export default function NumberField({
  id: idProp,
  label,
  error,
  helperText,
  size = 'medium',
  fullWidth,
  currencyField,
  weightFieldUnits,
  endAdornmentText,
  ...other
}: BaseNumberField.Root.Props & {
  label?: React.ReactNode;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  currencyField?: boolean;
  weightFieldUnits?: string;
  error?: boolean;
  helperText?: string;
  endAdornmentText?: string;
}) {
  let id = React.useId();

  if (idProp) {
    id = idProp;
  }

  const getEndAdornment = () => {
    if (weightFieldUnits) {
      return <InputAdornment position="end">{weightFieldUnits}</InputAdornment>;
    }

    if (endAdornmentText) {
      return <InputAdornment position="end">{endAdornmentText}</InputAdornment>;
    }

    return <></>;
  };

  return (
    <BaseNumberField.Root
      {...other}
      render={(props, state) => (
        <FormControl
          size={size}
          ref={props.ref}
          disabled={state.disabled}
          required={state.required}
          error={error}
          fullWidth={fullWidth}
          variant="outlined"
        >
          {props.children}
        </FormControl>
      )}
    >
      <SSRInitialFilled {...other} />
      <InputLabel
        htmlFor={id}
        shrink={true}>
        {label}
      </InputLabel>
      <BaseNumberField.Input
        id={id}
        render={(props, state) => (
          <OutlinedInput
            label={label}
            inputRef={props.ref}
            value={state.inputValue}
            onBlur={props.onBlur}
            onChange={props.onChange}
            onKeyUp={props.onKeyUp}
            onKeyDown={props.onKeyDown}
            onFocus={props.onFocus}
            fullWidth={fullWidth}
            startAdornment={currencyField ? <InputAdornment position="start">$</InputAdornment> : <></>}
            slotProps={{
              input: props
            }}
            notched
            endAdornment={getEndAdornment()}
          />
        )}
      />
      <FormHelperText sx={{ ml: 0, '&:empty': { mt: 0 } }}>
        {helperText}
      </FormHelperText>
    </BaseNumberField.Root>
  );
}